import {
	Conversation,
	type DisconnectionDetails,
	type Mode,
	type PartialOptions,
	type Role,
	type Status,
} from '@elevenlabs/client';
import { useCallback, useState } from 'react';

import { bridgeApi } from '@/api/BridgeApi.ts';
import { config } from '@/config/env';
import { logger } from '@/logger.ts';
import type { DbCustomLlmExtraBody } from '@/types.ts';

// 16-State Audio System Types
type AudioState =
	| 'speaker-off/mic-off'
	| 'speaker-off/mic-starting'
	| 'speaker-off/mic-stopping'
	| 'speaker-off/mic-on'
	| 'speaker-starting/mic-off'
	| 'speaker-starting/mic-starting'
	| 'speaker-starting/mic-stopping'
	| 'speaker-starting/mic-on'
	| 'speaker-stopping/mic-off'
	| 'speaker-stopping/mic-starting'
	| 'speaker-stopping/mic-stopping'
	| 'speaker-stopping/mic-on'
	| 'speaker-on/mic-off'
	| 'speaker-on/mic-starting'
	| 'speaker-on/mic-stopping'
	| 'speaker-on/mic-on';

type TransitionEvent =
	| 'speaker-request-start'
	| 'speaker-request-stop'
	| 'speaker-confirm-started'
	| 'speaker-confirm-stopped'
	| 'mic-request-start'
	| 'mic-request-stop'
	| 'mic-confirm-started'
	| 'mic-confirm-stopped';

type TransitionAction =
	| { type: 'startConversation' }
	| { type: 'endConversation' }
	| { type: 'setVolume'; volume: 0 | 1 }
	| { type: 'setMicMuted'; muted: boolean }
	| { type: 'none' };

type TransitionEntry = {
	nextState: AudioState;
	action: TransitionAction;
};

type TransitionTable = Map<string, TransitionEntry>;

export class ChatService {
	private readonly onUpdate: () => void;

	private userId: string | null = null;
	private elevenLabsConversation: Conversation | null = null;
	private temporarilyDisconnecting = false;

	private audioState: AudioState = 'speaker-off/mic-off';
	private transitionTable: TransitionTable;

	constructor(onUpdate: () => void) {
		this.onUpdate = onUpdate;
		this.transitionTable = this.buildTransitionTable();
	}

	private buildTransitionTable(): TransitionTable {
		const table = new Map<string, TransitionEntry>();

		// Helper to add transition
		const addTransition = (
			state: AudioState,
			event: TransitionEvent,
			nextState: AudioState,
			action: TransitionAction,
		) => {
			table.set(`${state}|${event}`, { nextState, action });
		};

		// S0M0 (speaker-off/mic-off) transitions
		addTransition(
			'speaker-off/mic-off',
			'speaker-request-start',
			'speaker-starting/mic-off',
			{
				type: 'startConversation',
			},
		);
		addTransition(
			'speaker-off/mic-off',
			'mic-request-start',
			'speaker-off/mic-starting',
			{
				type: 'startConversation',
			},
		);

		// S0M1 (speaker-off/mic-starting) transitions
		addTransition(
			'speaker-off/mic-starting',
			'speaker-request-start',
			'speaker-starting/mic-starting',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-off/mic-starting',
			'mic-confirm-started',
			'speaker-off/mic-on',
			{
				type: 'none',
			},
		);

		// S0M2 (speaker-off/mic-stopping) transitions
		addTransition(
			'speaker-off/mic-stopping',
			'speaker-request-start',
			'speaker-starting/mic-stopping',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-off/mic-stopping',
			'mic-confirm-stopped',
			'speaker-off/mic-off',
			{
				type: 'none',
			},
		);

		// S0M3 (speaker-off/mic-on) transitions
		addTransition(
			'speaker-off/mic-on',
			'speaker-request-start',
			'speaker-starting/mic-on',
			{
				type: 'setVolume',
				volume: 1,
			},
		);
		addTransition(
			'speaker-off/mic-on',
			'mic-request-stop',
			'speaker-off/mic-stopping',
			{
				type: 'endConversation',
			},
		);

		// S1M0 (speaker-starting/mic-off) transitions
		addTransition(
			'speaker-starting/mic-off',
			'speaker-confirm-started',
			'speaker-on/mic-off',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-starting/mic-off',
			'mic-request-start',
			'speaker-starting/mic-starting',
			{
				type: 'none',
			},
		);

		// S1M1 (speaker-starting/mic-starting) transitions
		addTransition(
			'speaker-starting/mic-starting',
			'speaker-confirm-started',
			'speaker-on/mic-starting',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-starting/mic-starting',
			'mic-confirm-started',
			'speaker-starting/mic-on',
			{
				type: 'none',
			},
		);

		// S1M2 (speaker-starting/mic-stopping) transitions
		addTransition(
			'speaker-starting/mic-stopping',
			'speaker-confirm-started',
			'speaker-on/mic-stopping',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-starting/mic-stopping',
			'mic-confirm-stopped',
			'speaker-starting/mic-off',
			{
				type: 'none',
			},
		);

		// S1M3 (speaker-starting/mic-on) transitions
		addTransition(
			'speaker-starting/mic-on',
			'speaker-confirm-started',
			'speaker-on/mic-on',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-starting/mic-on',
			'mic-request-stop',
			'speaker-starting/mic-stopping',
			{
				type: 'none',
			},
		);

		// S2M0 (speaker-stopping/mic-off) transitions
		addTransition(
			'speaker-stopping/mic-off',
			'speaker-confirm-stopped',
			'speaker-off/mic-off',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-stopping/mic-off',
			'mic-request-start',
			'speaker-stopping/mic-starting',
			{
				type: 'none',
			},
		);

		// S2M1 (speaker-stopping/mic-starting) transitions
		addTransition(
			'speaker-stopping/mic-starting',
			'speaker-confirm-stopped',
			'speaker-off/mic-starting',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-stopping/mic-starting',
			'mic-confirm-started',
			'speaker-stopping/mic-on',
			{
				type: 'none',
			},
		);

		// S2M2 (speaker-stopping/mic-stopping) transitions
		addTransition(
			'speaker-stopping/mic-stopping',
			'speaker-confirm-stopped',
			'speaker-off/mic-stopping',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-stopping/mic-stopping',
			'mic-confirm-stopped',
			'speaker-stopping/mic-off',
			{
				type: 'none',
			},
		);

		// S2M3 (speaker-stopping/mic-on) transitions
		addTransition(
			'speaker-stopping/mic-on',
			'speaker-confirm-stopped',
			'speaker-off/mic-on',
			{
				type: 'none',
			},
		);
		addTransition(
			'speaker-stopping/mic-on',
			'mic-request-stop',
			'speaker-stopping/mic-stopping',
			{
				type: 'none',
			},
		);

		// S3M0 (speaker-on/mic-off) transitions
		addTransition(
			'speaker-on/mic-off',
			'speaker-request-stop',
			'speaker-stopping/mic-off',
			{
				type: 'endConversation',
			},
		);
		addTransition(
			'speaker-on/mic-off',
			'mic-request-start',
			'speaker-on/mic-starting',
			{
				type: 'setMicMuted',
				muted: false,
			},
		);

		// S3M1 (speaker-on/mic-starting) transitions
		addTransition(
			'speaker-on/mic-starting',
			'speaker-request-stop',
			'speaker-stopping/mic-starting',
			{
				type: 'setVolume',
				volume: 0,
			},
		);
		addTransition(
			'speaker-on/mic-starting',
			'mic-confirm-started',
			'speaker-on/mic-on',
			{
				type: 'none',
			},
		);

		// S3M2 (speaker-on/mic-stopping) transitions
		addTransition(
			'speaker-on/mic-stopping',
			'speaker-request-stop',
			'speaker-stopping/mic-stopping',
			{
				type: 'setVolume',
				volume: 0,
			},
		);
		addTransition(
			'speaker-on/mic-stopping',
			'mic-confirm-stopped',
			'speaker-on/mic-off',
			{
				type: 'none',
			},
		);

		// S3M3 (speaker-on/mic-on) transitions
		addTransition(
			'speaker-on/mic-on',
			'speaker-request-stop',
			'speaker-stopping/mic-on',
			{
				type: 'setVolume',
				volume: 0,
			},
		);
		addTransition(
			'speaker-on/mic-on',
			'mic-request-stop',
			'speaker-on/mic-stopping',
			{
				type: 'setMicMuted',
				muted: true,
			},
		);

		return table;
	}

	private handleTransition = (event: TransitionEvent) => {
		const key = `${this.audioState}|${event}`;
		const transition = this.transitionTable.get(key);

		if (!transition) {
			logger.warn(`Invalid transition: ${this.audioState} + ${event}`);
			return;
		}

		const previousState = this.audioState;
		this.audioState = transition.nextState;

		logger.info(
			`State transition: ${previousState} + ${event} → ${this.audioState}`,
		);

		// Execute the action
		const { action } = transition;

		if (action.type === 'startConversation') {
			void this.startConversation();
		} else if (action.type === 'endConversation') {
			this.endConversation();
		} else if (action.type === 'setVolume') {
			if (this.elevenLabsConversation) {
				this.elevenLabsConversation.setVolume({ volume: action.volume });
				// Immediate confirmation for setVolume
				if (action.volume === 1) {
					this.handleTransition('speaker-confirm-started');
				} else {
					this.handleTransition('speaker-confirm-stopped');
				}
			}
		} else if (action.type === 'setMicMuted') {
			if (this.elevenLabsConversation) {
				this.elevenLabsConversation.setMicMuted(action.muted);
				// Immediate confirmation for setMicMuted
				if (action.muted) {
					this.handleTransition('mic-confirm-stopped');
				} else {
					this.handleTransition('mic-confirm-started');
				}
			}
		}
		// action.type === 'none' requires no operation

		this.onUpdate();
	};

	private getSpeakerState(): 'off' | 'starting' | 'stopping' | 'on' {
		const match = this.audioState.match(/^speaker-(off|starting|stopping|on)/);
		return match ? (match[1] as 'off' | 'starting' | 'stopping' | 'on') : 'off';
	}

	private getMicState(): 'off' | 'starting' | 'stopping' | 'on' {
		const match = this.audioState.match(/mic-(off|starting|stopping|on)$/);
		return match ? (match[1] as 'off' | 'starting' | 'stopping' | 'on') : 'off';
	}

	public getUserId() {
		return this.userId;
	}

	public setUserId = (userId: string | null) => {
		this.userId = userId;
		this.onUpdate();
	};

	public startDuplexConversation = async () => {
		if (this.audioState === 'speaker-off/mic-off') {
			logger.info('Starting duplex conversation (speaker + mic)');
			// Trigger both transitions synchronously before startConversation executes
			this.handleTransition('speaker-request-start');
			this.handleTransition('mic-request-start');
			// State is now speaker-starting/mic-starting
			// When startConversation completes, it will read this state and apply:
			// - volume=1 (speaker on)
			// - micMuted=false (mic on)
		} else {
			throw Error(
				`Called ChatService.startDuplexConversation while audioState is ${this.audioState}`,
			);
		}
	};

	public toggleSpeaker = () => {
		const speakerState = this.getSpeakerState();

		// Ignore if transitioning
		if (speakerState === 'starting' || speakerState === 'stopping') {
			logger.warn(`Ignoring speaker toggle while speaker is ${speakerState}`);
			return;
		}

		const event =
			speakerState === 'off' ? 'speaker-request-start' : 'speaker-request-stop';
		this.handleTransition(event);
	};

	public toggleMic = () => {
		const micState = this.getMicState();

		// Ignore if transitioning
		if (micState === 'starting' || micState === 'stopping') {
			logger.warn(`Ignoring mic toggle while mic is ${micState}`);
			return;
		}

		const event = micState === 'off' ? 'mic-request-start' : 'mic-request-stop';
		this.handleTransition(event);
	};

	public get isSpeakerOn() {
		return this.getSpeakerState() === 'on';
	}

	public get isMicOn() {
		return this.getMicState() === 'on';
	}

	public get isSpeakerTransitioning() {
		const state = this.getSpeakerState();
		return state === 'starting' || state === 'stopping';
	}

	public get isMicTransitioning() {
		const state = this.getMicState();
		return state === 'starting' || state === 'stopping';
	}

	private startConversation = async () => {
		if (!this.userId) {
			logger.error('No userId available in startConversation');
			return;
		}

		if (this.elevenLabsConversation) {
			throw Error(
				'Attempted to start conversation while another conversation is active',
			);
		}

		await navigator.mediaDevices.getUserMedia({ audio: true });

		const options: PartialOptions = {
			onConnect: (props: { conversationId: string }) => {
				logger.info('Connected to conversation:', props.conversationId);
				// Do NOT trigger state transitions here - wait until settings are applied
			},
			onDebug: (props: unknown) => {
				logger.debug('Debug info:', props);
			},
			onDisconnect: (details: DisconnectionDetails) => {
				logger.info('Disconnected, ending convo:', details);
				this.onDisconnect();
			},
			onError: (message: string, context?: unknown) => {
				logger.error('Error occurred:', { message, context });
			},
			onMessage: (props: { message: string; role: Role }) => {
				if (props.role === 'user') {
					const content =
						props.message.length <= 100
							? props.message
							: `${props.message.substring(0, 50)}...${props.message.substring(props.message.length - 50)}`;
					logger.info(
						`[LATENCY] user_transcript | ts=${new Date().toISOString()} | user_id=${this.userId} | content=${content}`,
					);
					// Send finalization signal - user finished speaking
					bridgeApi.sendAction({
						type: 'report_user_message_confirmed',
					});
				}
				logger.info(`Message received from role ${props.role}:`, props.message);
			},
			onInterruption: () => {
				logger.info('User interrupted agent speech');
				// Send interruption signal - user spoke over the agent
				bridgeApi.sendAction({
					type: 'report_agent_speech_interrupted',
				});
			},
			onAudio: (/* base64Audio: string */) => {
				logger.info('Audio received');
			},
			onModeChange: (prop: { mode: Mode }) => {
				logger.info('Mode changed to:', prop.mode);
			},
			onStatusChange: (prop: { status: Status }) => {
				logger.info('Status changed to:', prop.status);
			},
			onCanSendFeedbackChange: (prop: { canSendFeedback: boolean }) => {
				logger.info('Can send feedback changed to:', prop.canSendFeedback);
			},
			onUnhandledClientToolCall: (params: unknown) => {
				logger.warn('Unhandled client tool call:', params);
			},

			agentId: config.ELEVENLABS_AGENT_ID,
			connectionType: 'websocket',
			customLlmExtraBody: {
				user_id: this.userId,
			} satisfies DbCustomLlmExtraBody,
		};

		logger.info('Conversation options:', options);
		logger.info(
			`Starting conversation (settings will be applied based on current state)`,
		);

		// Start the session
		this.elevenLabsConversation = await Conversation.startSession(options);

		// Apply settings based on CURRENT state (not original params)
		// User might have clicked buttons during connection
		const speakerState = this.getSpeakerState();
		const micState = this.getMicState();

		// Determine what settings should be based on current state
		const currentVolume =
			speakerState === 'starting' || speakerState === 'on' ? 1 : 0;
		const currentMicMuted = micState === 'off' || micState === 'stopping';

		this.elevenLabsConversation.setVolume({ volume: currentVolume });
		this.elevenLabsConversation.setMicMuted(currentMicMuted);
		logger.info(
			`✓ Applied settings based on current state (${this.audioState}): volume=${currentVolume}, micMuted=${currentMicMuted}`,
		);

		// NOW trigger state transitions after settings are applied
		if (speakerState === 'starting') {
			this.handleTransition('speaker-confirm-started');
		}
		if (micState === 'starting') {
			this.handleTransition('mic-confirm-started');
		}

		bridgeApi.sendAction({
			type: 'report_elevenlabs_connected',
		});
	};

	private onDisconnect = () => {
		if (this.temporarilyDisconnecting) {
			return;
		}

		// Only trigger confirmation events for components in "stopping" state
		if (this.getSpeakerState() === 'stopping') {
			this.handleTransition('speaker-confirm-stopped');
		}
		if (this.getMicState() === 'stopping') {
			this.handleTransition('mic-confirm-stopped');
		}

		if (this.elevenLabsConversation) {
			void this.elevenLabsConversation.endSession();
			this.elevenLabsConversation = null;
		}

		bridgeApi.sendAction({
			type: 'report_elevenlabs_disconnected',
		});

		// Handle unexpected disconnect - force reset to off/off
		if (this.audioState !== 'speaker-off/mic-off') {
			logger.warn(
				'Unexpected disconnect, forcing state to speaker-off/mic-off',
			);
			this.audioState = 'speaker-off/mic-off';
			this.onUpdate();
		}
	};

	private endConversation = () => {
		if (this.elevenLabsConversation) {
			void this.elevenLabsConversation.endSession();
			this.elevenLabsConversation = null;
		}

		bridgeApi.sendAction({
			type: 'report_elevenlabs_disconnected',
		});
	};

	public endConversationExternally = () => {
		this.audioState = 'speaker-off/mic-off';
		this.endConversation();
		this.onUpdate();
	};
}

export const useChatService = () => {
	const [timestamp, setTimestamp] = useState<number>(0);
	const update = useCallback(() => {
		setTimestamp(Date.now());
	}, []);
	const [chatService] = useState(() => new ChatService(update));
	return [chatService, timestamp] as const;
};
